import React, { useState } from 'react'

import { useLocale, useT } from '../../i18n'
import type { FeedStopReason } from '../../utils/feedRules'

// Findings #210 / #211: the feed screen's "do not feed" block — a stop rule broken
// by today's last reading, or a fasting day before a planned move. The crew can
// still record when the supervisor authorises it (second tap); the record keeps why.

const formatNumber = (value: number, locale: string) => value.toLocaleString(locale, { maximumFractionDigits: 2 })

export const formatReadingTime = (at: string, locale: string, timeZone: string): string => {
  if (!at.includes('T')) return ''
  const ms = Date.parse(at)
  if (!Number.isFinite(ms)) return ''
  try {
    return new Date(ms).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone })
  } catch {
    return ''
  }
}

export const formatFarmDay = (day: string, locale: string): string => (
  new Date(`${day}T12:00:00Z`).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' })
)

export default function FeedHoldPanel(props: {
  reasons: FeedStopReason[]
  fastingMoveDate: string | null
  timeZone: string
  onMeasure: () => void
  onOverride: () => void
}) {
  const t = useT()
  const locale = useLocale()
  const [confirming, setConfirming] = useState(false)
  const stopped = props.reasons.length > 0

  return (
    <div
      data-testid="feed-capture-hold"
      role="alert"
      style={{ display: 'grid', gap: 10, padding: 14, borderRadius: 12, border: '2px solid #b91c1c', backgroundColor: '#fef2f2', color: '#7f1d1d' }}
    >
      {stopped ? (
        <>
          <strong data-testid="feed-capture-hold-stop" style={{ fontSize: 18 }}>⛔ {t('crewMode.feed.hold.stopTitle')}</strong>
          {props.reasons.map((reason) => (
            <span key={reason.kind} data-testid={`feed-capture-hold-${reason.kind}`} style={{ fontWeight: 700 }}>
              {t(`crewMode.feed.hold.${reason.kind}`, {
                value: formatNumber(reason.value, locale),
                limit: formatNumber(reason.limit, locale),
                time: formatReadingTime(reason.at, locale, props.timeZone)
              })}
              {reason.fromUnit ? ` · ${t('crewMode.feed.hold.measuredIn', { unit: reason.fromUnit })}` : ''}
            </span>
          ))}
          <span style={{ fontSize: 14 }}>{t('crewMode.feed.hold.stopHelp')}</span>
        </>
      ) : null}
      {props.fastingMoveDate ? (
        <>
          <strong data-testid="feed-capture-hold-fasting" style={{ fontSize: 18 }}>🚫 {t('crewMode.feed.hold.fastTitle')}</strong>
          <span style={{ fontSize: 14 }}>{t('crewMode.feed.hold.fastHelp', { date: formatFarmDay(props.fastingMoveDate, locale) })}</span>
        </>
      ) : null}
      {stopped ? (
        <button
          type="button"
          data-testid="feed-capture-hold-measure"
          onClick={props.onMeasure}
          style={{ minHeight: 48, borderRadius: 10, border: '1px solid #1d4ed8', backgroundColor: 'white', color: '#1d4ed8', fontWeight: 800, fontSize: 15 }}
        >
          {t('crewMode.feed.hold.measure')}
        </button>
      ) : null}
      {confirming ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{t('crewMode.feed.hold.overrideConfirm')}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              style={{ flex: 1, minHeight: 44, borderRadius: 10, border: '1px solid #d1d5db', backgroundColor: 'white', fontWeight: 700 }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              data-testid="feed-capture-hold-override-yes"
              onClick={props.onOverride}
              style={{ flex: 1, minHeight: 44, borderRadius: 10, border: '1px solid #b91c1c', backgroundColor: '#b91c1c', color: 'white', fontWeight: 800 }}
            >
              {t('crewMode.feed.hold.overrideYes')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="feed-capture-hold-override"
          onClick={() => setConfirming(true)}
          style={{ border: 'none', background: 'none', padding: 0, color: '#7f1d1d', textDecoration: 'underline', fontSize: 13, textAlign: 'left', cursor: 'pointer' }}
        >
          {t('crewMode.feed.hold.override')}
        </button>
      )}
    </div>
  )
}
